import { POSITIONS, positionLabelKey, normalizePosition, legacyRankOf, outranks } from "../access/positions";
// Admin user list — paginated, filterable table that mirrors the
// LocationsAdmin / TotesAdmin pattern so the admin section feels like
// one consistent product. Replaces the prior mui-datatables wrapper
// which dragged in a heavy theme provider just to render six columns.
import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, KeyRound, UserX, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

import { displayUserType, userNeedsAccountSetup } from "../types/User";
import { passwordExpired, PASSWORD_MAX_AGE_DAYS } from "./helpers/passwordPolicy";
import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import FilterPills from "./admin/FilterPills";
import { useReadOnly } from "../access/ReadOnly";
import { useAccess } from "../access/AccessContext";
import { useWarehouseScope } from "../access/useWarehouseScope";

// Identity of the signed-in admin so the row actions can hide buttons
// that would act on self (forcing your own password, suspending
// yourself). Read once at render time; the localStorage value is
// session-stable.
const readSelfUid = (): number | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const u = JSON.parse(raw);
        return typeof u?.uid === "number" ? u.uid : null;
    } catch { return null; }
};

// Role of the signed-in admin. superAdmin rows only show an edit
// pencil to other superAdmins — plain admins can't touch them at all
// (the BE enforces the same rule on /login/user/edit).
const readSelfRole = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        return typeof u?.permissions === "string" ? u.permissions : "";
    } catch { return ""; }
};

const PAGE_SIZE = 25;

// Sortable columns. `name` is the natural default — admins are usually
// looking for a specific person rather than scanning by recency.
type SortField = "name" | "username" | "warehouse" | "permissions" | "lastActiveAt" | "passwordChangedAt" | "createdAt";
type SortDir = "asc" | "desc";

// Sort key for the Position column: the normalised position name, so a
// legacy rank on an un-migrated document sorts with the position it maps
// to. Display labels go through t(positionLabelKey(...)) in the row.
const formatPermissions = (raw: string): string => {
    if (!raw) return "";
    return normalizePosition(raw) ?? raw;
};

// Compact "DD MMM YY" formatter for the Last login / Password set
// columns. Returns "—" for absent or unparseable timestamps so legacy
// docs without these fields render cleanly. Uses the browser locale's
// short month so the format matches whatever the rest of the page uses.
const formatStamp = (iso?: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
};

// Read the signed-in user's warehouse so we can pre-filter the table
// to their site on first load. Returns "" when the session is missing
// or malformed — the table then shows everyone.
const readSignedInWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        return typeof u?.warehouse === "string" ? u.warehouse : "";
    } catch { return ""; }
};

interface UserListProps {
    users: displayUserType[];
    toggleDelete: (user: displayUserType | null) => void;
    toggleEdit: ({ user, changesMade }: { user: displayUserType, changesMade: boolean }) => void;
    /** Open the confirmation modal for: forcing a password rotation,
     *  suspending the user, or reinstating a suspended user. The page
     *  owns the actual API calls so the same confirm modal can be
     *  triggered from both the table and the edit modal. */
    requestForce: (user: displayUserType) => void;
    requestSuspend: (user: displayUserType) => void;
    requestUnsuspend: (user: displayUserType) => void;
}

const UserList = ({ users, toggleDelete, toggleEdit, requestForce, requestSuspend, requestUnsuspend }: UserListProps) => {
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    // Edit and deactivate have their own keys (admin.users.edit / .deactivate, 2026-09-30).
    const { authoritative, canWrite } = useAccess();
    const canEdit = !readOnly && (!authoritative || canWrite("admin.users.edit"));
    const canDeactivate = !readOnly && (!authoritative || canWrite("admin.users.deactivate"));
    const selfUid = useMemo(readSelfUid, []);
    // Who the viewer may manage: permissions run downstream (access/positions outranks).
    const selfPermissions = useMemo(readSelfRole, []);
    // `?search=` URL param lets AdminSearch deep-link in pre-filtered.
    const [search, setSearch] = useUrlSearchParam("search");
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<string>(readSignedInWarehouse);
    // Without cross_warehouse the list is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [permissionFilter, setPermissionFilter] = useState<string>("");
    const [sortField, setSortField] = useState<SortField>("name");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const [page, setPage] = useState(0);

    // Reset to page 1 on filter or sort change so the operator never
    // lands on an empty page after narrowing the results.
    useEffect(() => {
        setPage(0);
    }, [search, warehouseFilter, permissionFilter, sortField, sortDir]);

    // Click a header to sort by that column. Same column toggles
    // direction; new column starts ascending for text fields and
    // descending for date fields (most useful "newest first" default).
    const onHeaderSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            const isDateField = field === "lastActiveAt" || field === "passwordChangedAt" || field === "createdAt";
            setSortDir(isDateField ? "desc" : "asc");
        }
    };

    // Warehouse counts feed the pill row — built off the unfiltered set
    // so the badges stay stable as the operator clicks between pills.
    const warehouseCounts = useMemo(() => {
        const out: Record<string, number> = {};
        for (const u of users) if (u.warehouse) out[u.warehouse] = (out[u.warehouse] ?? 0) + 1;
        return out;
    }, [users]);

    const warehouses = useMemo(
        () => Object.keys(warehouseCounts).sort().filter(w => warehouseScope.canCross || w === warehouseScope.own),
        [warehouseCounts, warehouseScope.canCross, warehouseScope.own],
    );

    // Position counts from the unfiltered set, same pattern as
    // warehouseCounts. Keyed by NORMALISED position so a legacy rank on
    // an un-migrated document counts under the position it maps to.
    const permissionCounts = useMemo(() => {
        const out: Record<string, number> = Object.fromEntries(POSITIONS.map(p => [p, 0]));
        for (const u of users) {
            const p = normalizePosition(u.permissions);
            if (p) out[p] += 1;
        }
        return out;
    }, [users]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter(u => {
            if (warehouseFilter && u.warehouse !== warehouseFilter) return false;
            if (permissionFilter && normalizePosition(u.permissions) !== permissionFilter) return false;
            if (!q) return true;
            return (
                (u.name ?? "").toLowerCase().includes(q) ||
                (u.username ?? "").toLowerCase().includes(q) ||
                (u.permissions ?? "").toLowerCase().includes(q) ||
                (u.warehouse ?? "").toLowerCase().includes(q) ||
                (u.userRFID ?? "").toLowerCase().includes(q)
            );
        });
    }, [users, search, warehouseFilter, permissionFilter]);

    // Sort happens after filter so the count + range labels match what
    // the user sees on screen. Empty / null values always sort to the
    // end regardless of direction — a never-logged-in row shouldn't
    // pollute the top of an asc-by-date sort.
    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        const out = filtered.slice();
        out.sort((a, b) => {
            const va = sortField === "name"
                ? (a.name ?? "")
                : sortField === "username"
                    ? (a.username ?? "")
                    : sortField === "warehouse"
                        ? (a.warehouse ?? "")
                        : sortField === "permissions"
                            ? formatPermissions(a.permissions)
                            : (a as displayUserType)[sortField] ?? "";
            const vb = sortField === "name"
                ? (b.name ?? "")
                : sortField === "username"
                    ? (b.username ?? "")
                    : sortField === "warehouse"
                        ? (b.warehouse ?? "")
                        : sortField === "permissions"
                            ? formatPermissions(b.permissions)
                            : (b as displayUserType)[sortField] ?? "";
            // Empty / null → end, regardless of direction.
            const aEmpty = va === "" || va == null;
            const bEmpty = vb === "" || vb == null;
            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return 1;
            if (bEmpty) return -1;
            // Date fields compare lexicographically when ISO-formatted,
            // which agrees with chronological order — no need for
            // Date.parse here.
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
        return out;
    }, [filtered, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const sliceStart = safePage * PAGE_SIZE;
    const sliceEnd = sliceStart + PAGE_SIZE;
    const visible = sorted.slice(sliceStart, sliceEnd);
    const rangeFrom = sorted.length === 0 ? 0 : sliceStart + 1;
    const rangeTo = Math.min(sliceEnd, sorted.length);

    // Inline sortable header — chevron always visible (faded when
    // inactive) so the affordance reads at a glance, switches to
    // up/down + primary colour on the active column.
    const SortableTh = ({ field, label, width }: { field: SortField; label: string; width?: number }) => {
        const active = sortField === field;
        const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
        const ariaSort = !active ? "none" : sortDir === "asc" ? "ascending" : "descending";
        return (
            <th
                style={width ? { width } : undefined}
                aria-sort={ariaSort}
                className={`users-th-sort${active ? " users-th-sort--active" : ""}`}
            >
                <button
                    type="button"
                    className="users-th-sort-btn"
                    onClick={() => onHeaderSort(field)}
                >
                    <span>{label}</span>
                    <Icon size={12} className="users-th-sort-icon" aria-hidden />
                </button>
            </th>
        );
    };

    return (
        <div className="users-admin">
            {/* Two labelled toggle groups on a single row (wraps on
                narrow viewports). Each group shows what it filters
                ("Warehouse" / "Permissions") so admins don't have to
                infer the dimension from the pill values. */}
            <div className="users-admin-filters">
                <input
                    className="locations-table-search users-admin-filters-search"
                    type="search"
                    placeholder={t("users_filter_search")}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <FilterPills
                    labelKey="warehouse"
                    value={warehouseFilter}
                    onChange={setWarehouseFilter}
                    allCount={users.length}
                    hideAll={!warehouseScope.canCross}
                    options={warehouses.map(w => ({ value: w, label: w, count: warehouseCounts[w] }))}
                />
                <FilterPills
                    labelKey="users_col_permissions"
                    value={permissionFilter}
                    onChange={setPermissionFilter}
                    allCount={users.length}
                    options={POSITIONS
                        .filter(p => permissionCounts[p] > 0)
                        .map(p => ({ value: p, label: t(positionLabelKey(p)), count: permissionCounts[p] }))}
                />
            </div>

            {filtered.length === 0 ? (
                <div className="locations-table-empty">{t("users_no_matches")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap">
                        <table className="locations-table">
                            <thead>
                                <tr>
                                    <SortableTh field="name"              label={t("users_col_name")} />
                                    <SortableTh field="username"          label={t("users_col_username")} width={150} />
                                    <SortableTh field="warehouse"         label={t("users_col_warehouse")} width={180} />
                                    <SortableTh field="permissions"       label={t("users_col_permissions")} width={140} />
                                    <SortableTh field="createdAt"         label={t("users_col_created")} width={130} />
                                    <SortableTh field="lastActiveAt"      label={t("users_col_last_active")} width={130} />
                                    <SortableTh field="passwordChangedAt" label={t("users_col_password_changed")} width={130} />
                                    <th style={{ width: 110, textAlign: "right" }} aria-label="" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(u => {
                                    const isSelf = selfUid != null && u.UID === selfUid;
                                    const isSuspended = u.status === "suspended";
                                    // Permissions run downstream (2026-10-20): an Admin manages every
                                    // account below Admin, a Manager every account below Manager, and
                                    // nobody touches a peer. Your own row only opens the editor (own
                                    // name / email / username / warehouse). The server enforces the same.
                                    const manages = outranks(selfPermissions, u.permissions);
                                    const showForceChange = manages && !isSelf && u.mustChangePassword !== true;
                                    const showSuspendToggle = manages && !isSelf;
                                    const showEdit = manages || isSelf;
                                    const showDelete = manages && !isSelf;
                                    return (
                                        <tr
                                            key={u._id || u.UID}
                                            className={isSuspended ? "users-admin-row--suspended" : undefined}
                                        >
                                            <td>{u.name}</td>
                                            <td>
                                                {/* Login handle. An account without one still signs in by
                                                    colour + PIN and is badged so an admin can see who has
                                                    not been through account setup yet. */}
                                                {userNeedsAccountSetup(u) ? (
                                                    <span className="users-admin-permission users-admin-permission--setup" title={t("users_needs_setup_hint")}>
                                                        {t("users_needs_setup")}
                                                    </span>
                                                ) : (
                                                    <code className="users-admin-username">{u.username}</code>
                                                )}
                                            </td>
                                            <td>{u.warehouse || "—"}</td>
                                            <td>
                                                {/* Colour class stays keyed by legacy rank (existing CSS: basic / admin /
                                                    superAdmin); the label is the translated position name. */}
                                                <span className={`users-admin-permission users-admin-permission--${legacyRankOf(u.permissions)}`}>
                                                    {(() => { const p = normalizePosition(u.permissions); return p ? t(positionLabelKey(p)) : u.permissions; })()}
                                                </span>
                                                {u.mustChangePassword === true && !isSuspended && (
                                                    <span className="users-admin-permission users-admin-permission--rotate" title={t("users_rotation_pending_hint")}>
                                                        {t("users_rotation_pending")}
                                                    </span>
                                                )}
                                                {/* Password age (2026-10-20): set more than 90 days ago, or never
                                                    stamped — the BE forces a new one at their next sign-in. Hidden
                                                    behind the setup / rotation badges, which already say so. */}
                                                {u.mustChangePassword !== true && !isSuspended && !userNeedsAccountSetup(u) && passwordExpired(u.passwordChangedAt) && (
                                                    <span className="users-admin-permission users-admin-permission--expired" title={t("users_password_expired_hint", { days: String(PASSWORD_MAX_AGE_DAYS) })}>
                                                        {t("users_password_expired")}
                                                    </span>
                                                )}
                                                {/* Two-factor (2026-10-20): only positions that are challenged
                                                    get a chip — green once an authenticator is set up, amber
                                                    while the user still has to enrol at their next sign-in. */}
                                                {u.twoFactorRequired === true && !isSuspended && (
                                                    u.twoFactorEnrolledAt ? (
                                                        <span className="users-admin-permission users-admin-permission--tfa" title={t("users_two_factor_enrolled_hint")}>
                                                            {t("users_two_factor_pill")}
                                                        </span>
                                                    ) : (
                                                        <span className="users-admin-permission users-admin-permission--tfa-pending" title={t("users_two_factor_pending_hint")}>
                                                            {t("users_two_factor_pending_pill")}
                                                        </span>
                                                    )
                                                )}
                                            </td>
                                            <td>{formatStamp(u.createdAt)}</td>
                                            <td>{formatStamp(u.lastActiveAt)}</td>
                                            <td>{formatStamp(u.passwordChangedAt)}</td>
                                            <td className="locations-table-actions">
                                                {showForceChange && (
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn"
                                                        onClick={() => requestForce(u)}
                                                        disabled={readOnly}
                                                        aria-disabled={readOnly}
                                                        aria-label={t("users_force_change")}
                                                        title={readOnly ? t("read_only_mode") : t("users_force_change")}
                                                    >
                                                        <KeyRound size={14} />
                                                    </button>
                                                )}
                                                {showSuspendToggle && (
                                                    <button
                                                        type="button"
                                                        className={`locations-table-btn${isSuspended ? " locations-table-btn--suspended" : ""}`}
                                                        onClick={() => isSuspended ? requestUnsuspend(u) : requestSuspend(u)}
                                                        disabled={readOnly}
                                                        aria-disabled={readOnly}
                                                        aria-label={t(isSuspended ? "users_unsuspend" : "users_suspend")}
                                                        title={readOnly ? t("read_only_mode") : t(isSuspended ? "users_unsuspend" : "users_suspend")}
                                                    >
                                                        <UserX size={14} />
                                                    </button>
                                                )}
                                                {showEdit && (
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn"
                                                        onClick={() => toggleEdit({ user: u, changesMade: true })}
                                                        disabled={!canEdit}
                                                        aria-disabled={!canEdit}
                                                        aria-label={t("edit")}
                                                        title={readOnly ? t("read_only_mode") : !canEdit ? t("users_action_not_granted") : t("edit")}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                )}
                                                {showDelete && (
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn locations-table-btn--delete"
                                                        onClick={() => toggleDelete(u)}
                                                        disabled={!canDeactivate}
                                                        aria-disabled={!canDeactivate}
                                                        aria-label={t("delete")}
                                                        title={readOnly ? t("read_only_mode") : !canDeactivate ? t("users_action_not_granted") : t("delete")}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(filtered.length),
                        })}</span>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default UserList;
